import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { VerseBlock } from "@/components/VerseBlock";
import { toast } from "sonner";
import { Check, MessageSquare, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { ConcernPicker, toggleConcern, SourceSelector } from "@/lib/scriptureUi";

const FREQ = [
  { value: "daily", label: "Every morning" }, { value: "few_days", label: "Every few days" }, { value: "weekly", label: "Weekly" },
];

export function DailyProverb() {
  const [data, setData] = useState(null);
  const [topics, setTopics] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("proverbs");
  const [frequency, setFrequency] = useState("daily");
  const [channel, setChannel] = useState("email");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => api.get("/daily-proverb").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { api.get("/topics").then((r) => setTopics(r.data)).catch(() => {}); load(); }, []);

  const toggle = (slug, max) => setChosen((c) => toggleConcern(c, slug, max));

  const subscribe = async () => {
    if (channel === "email" && !email) { toast.error("Enter your email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wisdom/subscribe", {
        email: channel === "email" ? email : (email || "pending@ineedaproverb.com"),
        concerns: chosen, source, frequency, channel,
      });
      track("recurring_optin", { path: "/daily", topic: chosen[0] });
      setDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not subscribe."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0 -z-10"><img src="/assets/content-hero.jpg" alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-[#FDFBF7]/90" /></div>
        <div className="max-w-3xl mx-auto px-5 py-16 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] mb-3">Free to use</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A]">Wisdom for You</h1>
          <p className="text-[#475569] mt-3">A little wisdom, right when you need it.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 -mt-2 pb-10">
        {data && (
          <>
            <VerseBlock v={data.verse} index={0} />
            <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F9F7F1] px-6 py-5">
              <div className="text-[10px] uppercase tracking-widest text-[#B8912F] font-semibold mb-1">One action for today</div>
              <p className="text-[15px] text-[#334155] leading-relaxed">{data.action}</p>
            </div>
          </>
        )}

        <div className="mt-10 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-7">
          {done ? (
            <div data-testid="daily-subscribed-message" className="text-center py-6">
              <div className="mx-auto mb-4 grid place-items-center w-12 h-12 rounded-full bg-[#F1F5F1]"><Check className="w-6 h-6 text-[#2C4C3B]" /></div>
              <p className="font-serif-display text-2xl text-[#0F172A]">{done.coming_soon ? "Saved" : "You're all set."}</p>
              <p className="text-[#475569] mt-1">{done.message}</p>
              <p className="text-xs text-[#94A3B8] mt-4">You can change or stop anytime from the link in each message.</p>
            </div>
          ) : (
            <>
              <h3 className="font-serif-display text-2xl text-[#0F172A] mb-1">Receive wisdom, your way</h3>
              <p className="text-sm text-[#475569] mb-5">Choose what you'd like and how often. Stop or change it anytime.</p>

              <Label>What are you facing? <span className="font-normal text-[#94A3B8]">(up to 3)</span></Label>
              <div data-testid="daily-concerns" className="mb-6">
                <ConcernPicker topics={topics} selected={chosen} onToggle={toggle} testidPrefix="daily-concern" />
              </div>

              <Label>What would you like to receive?</Label>
              <div className="mb-6"><SourceSelector value={source} onChange={setSource} testidPrefix="daily-src" /></div>

              <Label>How often?</Label>
              <div className="flex flex-wrap gap-2 mb-6">
                {FREQ.map((o) => (
                  <button key={o.value} data-testid={`daily-freq-${o.value}`} onClick={() => setFrequency(o.value)}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${frequency === o.value ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#0F172A]"}`}>{o.label}</button>
                ))}
              </div>

              <Label>Delivery</Label>
              <div className="flex flex-wrap gap-2 mb-4">
                <SegBtn testid="daily-channel-email" active={channel === "email"} onClick={() => setChannel("email")}>Email</SegBtn>
                <SegBtn testid="daily-channel-text" active={channel === "text"} onClick={() => setChannel("text")}><MessageSquare className="w-4 h-4 inline mr-1" /> Text — Coming Soon</SegBtn>
              </div>

              <input data-testid="daily-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type={channel === "email" ? "email" : "tel"}
                placeholder={channel === "email" ? "you@example.com" : "your phone number"}
                className="w-full rounded-full border border-[#E2E8F0] px-4 py-3 focus:outline-none focus:border-[#2C4C3B] mb-3" />
              {channel === "text" && <p className="text-xs text-[#B8912F] mb-3">Text delivery is coming soon — we'll save your choice and notify you.</p>}

              <button data-testid="daily-subscribe-btn" onClick={subscribe} disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#2C4C3B] text-white font-semibold disabled:opacity-60">
                {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</> : "Start receiving wisdom"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Label({ children }) { return <p className="text-sm font-semibold text-[#2C4C3B] mb-2">{children}</p>; }
function SegBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-[#2C4C3B] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#2C4C3B]"}`}>{children}</button>
  );
}

export function DownloadLibrary() {
  const [guides, setGuides] = useState([]);
  useEffect(() => { api.get("/guides").then((r) => setGuides(r.data)); }, []);
  const download = async (slug, title) => {
    try {
      const res = await api.get(`/guides/${slug}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${slug}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloading ${title}`);
    } catch { toast.error("Could not download."); }
  };
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif-display text-4xl text-[#0F172A] mb-2">Download Library</h1>
      <p className="text-[#475569] mb-10">Beautiful, printer-friendly guides drawn from Proverbs.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {guides.map((g) => (
          <div key={g.slug} data-testid={`guide-${g.slug}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-[#F1F5F1] text-[#2C4C3B]">Free</span>
            </div>
            <h3 className="font-serif-display text-2xl text-[#0F172A] leading-snug mb-2">{g.title}</h3>
            <p className="text-sm text-[#475569] leading-relaxed flex-1">{g.description}</p>
            <button data-testid={`download-guide-${g.slug}`} onClick={() => download(g.slug, g.title)}
              className="mt-5 w-full py-2.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-medium hover:bg-[#F1F5F1] transition-colors">Download PDF</button>
          </div>
        ))}
      </div>
    </div>
  );
}
